import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, SkipForward } from "lucide-react";
import { useLang } from "@/App";
import { Logo } from "@/components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export default function Intro({ onDone }) {
    const { t } = useLang();
    const [phase, setPhase] = useState(0);
    const [hasVideo, setHasVideo] = useState(false);
    const audioRef = useRef(null);
    const videoRef = useRef(null);
    const runningRef = useRef(false);

    useEffect(() => {
        fetch("/videos/intro.mp4", { method: "HEAD" })
            .then((r) => setHasVideo(r.ok && (r.headers.get("content-type") || "").includes("video")))
            .catch(() => {});
    }, []);

    const playLine = async (text, persona) => {
        try {
            const res = await fetch(`${API}/tts`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ text, persona }),
            });
            if (!res.ok) return;
            const blob = await res.blob();
            const a = new Audio(URL.createObjectURL(blob));
            audioRef.current?.pause();
            audioRef.current = a;
            a.play().catch(() => {});
        } catch {
            /* silent */
        }
    };

    const finish = () => {
        audioRef.current?.pause();
        videoRef.current?.pause();
        localStorage.setItem("neon_intro_seen", "1");
        onDone();
    };

    const start = async () => {
        if (runningRef.current) return;
        runningRef.current = true;
        setPhase(1);
        videoRef.current?.play().catch(() => {});
        playLine(t.intro.lucyLine, "lucy");
        await wait(2400);
        setPhase(2);
        playLine(t.intro.davidLine, "david");
        await wait(2400);
        setPhase(3);
        await wait(1900);
        finish();
    };

    return (
        <motion.div
            className="fixed inset-0 z-[100] bg-[#05060d] flex flex-col overflow-hidden"
            data-testid="intro-overlay"
            exit={{ opacity: 0 }}
        >
            <div className="absolute inset-0 speedlines opacity-60" />
            <div className="absolute left-1/3 top-1/4 w-[380px] h-[380px] rounded-full bg-[#ff4bd8]/10 blur-[110px]" />
            <div className="absolute right-1/4 bottom-1/4 w-[380px] h-[380px] rounded-full bg-[#49f5e8]/10 blur-[110px]" />

            <div className="h-[7vh] bg-black/80 border-b border-[#fcee0a]/25 relative z-10" />
            <div className="warning-stripes h-1.5 opacity-70 relative z-10" />

            <div className="flex-1 relative flex items-center justify-center z-10 px-6">
                <span className="kana kana-vertical absolute left-5 top-1/2 -translate-y-1/2 text-[#49f5e8]/40 text-base hidden sm:block">ナイトシティ</span>
                <span className="kana kana-vertical absolute right-5 top-1/2 -translate-y-1/2 text-[#ff4bd8]/40 text-base hidden sm:block">エッジランナー</span>

                {phase === 0 && (
                    <motion.button
                        data-testid="intro-start-btn"
                        onClick={start}
                        initial={{ opacity: 0, scale: 0.94 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex flex-col items-center gap-6 group"
                    >
                        <Logo size={72} />
                        <span className="flex items-center gap-3 bg-[#fcee0a] text-[#080a12] font-bold text-sm px-8 py-4 rounded-full group-hover:shadow-[0_0_44px_rgba(252,238,10,0.6)] transition-shadow">
                            <Play size={16} />
                            {t.intro.start}
                            <span className="font-mono2 text-[9px] tracking-[0.2em] opacity-70">· {t.intro.hint}</span>
                        </span>
                    </motion.button>
                )}

                {phase >= 1 && hasVideo && (
                    <div className="absolute inset-0" data-testid="intro-video-wrap">
                        <video
                            ref={videoRef}
                            src="/videos/intro.mp4"
                            muted
                            playsInline
                            className="w-full h-full object-cover"
                            data-testid="intro-video"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#05060d]/85 via-transparent to-[#05060d]/60" />
                        <div className="absolute bottom-[8%] left-1/2 -translate-x-1/2 w-max max-w-[92%]">
                            <AnimatePresence mode="wait">
                                {phase === 1 && (
                                    <motion.div
                                        key="l1"
                                        initial={{ opacity: 0, y: 14 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="bg-[#0b0f1a]/85 backdrop-blur border border-[#ff4bd8]/50 rounded-2xl px-6 py-3.5 text-lg sm:text-2xl text-center"
                                        data-testid="intro-lucy-line"
                                    >
                                        <span className="script-pink mr-3 text-2xl">Lucy</span>
                                        {t.intro.lucyLine}
                                    </motion.div>
                                )}
                                {phase >= 2 && (
                                    <motion.div
                                        key="d1"
                                        initial={{ opacity: 0, y: 14 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="bg-[#0b0f1a]/85 backdrop-blur border border-[#fcee0a]/50 rounded-2xl px-6 py-3.5 text-lg sm:text-2xl text-center"
                                        data-testid="intro-david-line"
                                    >
                                        <span className="script-neon mr-3 text-2xl">David</span>
                                        {t.intro.davidLine}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                )}

                {phase >= 1 && !hasVideo && (
                    <div className="w-full max-w-3xl flex items-end justify-between gap-4">
                        <AnimatePresence>
                            <motion.div
                                key="lucy"
                                initial={{ opacity: 0, x: -70 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                                className="flex flex-col items-center gap-4"
                                data-testid="intro-lucy"
                            >
                                <div className={`w-28 h-28 sm:w-40 sm:h-40 rounded-full overflow-hidden border-2 transition-all duration-500 ${phase === 1 ? "border-[#ff4bd8] shadow-[0_0_50px_rgba(255,75,216,0.5)]" : "border-[#2c3a5c] opacity-70"}`}>
                                    <img src="/char/av-lucy.png" alt="Lucy" className="w-full h-full object-cover" />
                                </div>
                                {phase === 1 && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 14 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="bg-[#131a2c] border border-[#ff4bd8]/50 rounded-2xl rounded-bl-sm px-5 py-3 text-base sm:text-lg"
                                        data-testid="intro-lucy-line"
                                    >
                                        {t.intro.lucyLine}
                                    </motion.div>
                                )}
                                <span className="script-pink text-2xl">Lucy</span>
                            </motion.div>
                        </AnimatePresence>

                        {phase === 3 && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="absolute left-1/2 -translate-x-1/2 -top-2 sm:top-0 font-display font-black tracking-[0.3em] text-sm sm:text-base text-center"
                                data-testid="intro-outro"
                            >
                                <span className="neon-text">KIBER AI</span>
                                <span className="text-slate-500"> · </span>
                                <span className="edg-text">EDGERUNNERS</span>
                            </motion.div>
                        )}

                        <AnimatePresence>
                            {phase >= 2 && (
                                <motion.div
                                    key="david"
                                    initial={{ opacity: 0, x: 70 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                                    className="flex flex-col items-center gap-4"
                                    data-testid="intro-david"
                                >
                                    <div className={`w-28 h-28 sm:w-40 sm:h-40 rounded-full overflow-hidden border-2 transition-all duration-500 ${phase === 2 ? "border-[#fcee0a] shadow-[0_0_50px_rgba(252,238,10,0.5)]" : "border-[#2c3a5c] opacity-70"}`}>
                                        <img src="/char/av-david.png" alt="David" className="w-full h-full object-cover" />
                                    </div>
                                    {phase === 2 && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 14 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="bg-[#131a2c] border border-[#fcee0a]/50 rounded-2xl rounded-br-sm px-5 py-3 text-base sm:text-lg"
                                            data-testid="intro-david-line"
                                        >
                                            {t.intro.davidLine}
                                        </motion.div>
                                    )}
                                    <span className="script-neon text-2xl">David</span>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                )}

                {phase === 3 && hasVideo && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="absolute top-6 left-1/2 -translate-x-1/2 font-display font-black tracking-[0.3em] text-sm sm:text-base"
                        data-testid="intro-outro"
                    >
                        <span className="neon-text">KIBER AI</span>
                        <span className="text-slate-500"> · </span>
                        <span className="edg-text">EDGERUNNERS</span>
                    </motion.div>
                )}
            </div>

            <div className="warning-stripes h-1.5 opacity-70 relative z-10" />
            <div className="h-[7vh] bg-black/80 border-t border-[#fcee0a]/25 relative z-10 flex items-center justify-end px-6">
                <button
                    data-testid="intro-skip-btn"
                    onClick={finish}
                    className="flex items-center gap-2 font-mono2 text-[10px] tracking-[0.25em] text-slate-500 hover:text-[#fcee0a] transition-colors"
                >
                    {t.intro.skip} <SkipForward size={13} />
                </button>
            </div>
        </motion.div>
    );
}
